import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { App, Button, Input, Select, Space, Table, Tag, Typography } from "antd";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { db } from "../lib/firebase";
import ProjectDetailsModal from "../components/ProjectDetailsModal";
import { TableSkeleton } from "../components/TableSkeleton";
import { EmptyState } from "../components/EmptyState";
import { StatusPill, type StatusTone } from "../components/StatusPill";
import { useApexContext } from "../components/apex/ApexProvider";
import type { TaskItem, MetricItem, SubtaskItem } from "../lib/types";
import { getWorkflow, updateProject } from "../lib/api";
import { type IntakeSource } from "../components/intakeDetails/types";
import { CLOSED_STATUSES, statusOptions } from "../components/projectDetails/types";

const FLAT_STATUS_OPTIONS = statusOptions.flatMap((group) => group.options);
const DEFAULT_STATUSES = FLAT_STATUS_OPTIONS.filter((status) => !CLOSED_STATUSES.has(status));

interface ProjectItem {
  id: string;
  name: string;
  status: string;
  source?: IntakeSource;
  startDate?: string;
  endDate?: string;
  vendorName?: string;
  vendorPartners?: string[];
  intakeId?: string;
  moaFileUrl?: string;
  createdAt?: string;
  aiSummary?: string;
  aiSummaryUpdatedAt?: string;
  aiHeadline?: string;
  aiHeadlineUpdatedAt?: string;
}

interface CommentItem {
  id: string;
  projectId: string;
  message: string;
  createdAt?: string;
  author?: string;
}

interface AttachmentItem {
  id: string;
  projectId: string;
  name: string;
  url: string;
  type?: string;
  createdAt?: string;
}

interface WorkflowStage {
  id: string;
  name: string;
  status: string;
}

interface WorkflowData {
  id: string;
  intakeId: string;
  currentStage: string;
  stages: WorkflowStage[];
}

interface MeetingData {
  id: string;
  intakeId: string;
  workflowId?: string;
  meetingDate: string;
  meetingType: string;
  status: string;
  attendees?: string[];
  notes?: string;
}

interface ScoringData {
  id: string;
  intakeId: string;
  category: string;
  criteria: string;
  score: number;
  notes?: string;
}

interface Vendor {
  id: string;
  profile?: string;
  capabilityTags?: string[];
  status?: string;
}

export default function Projects() {
  const { setPageContext } = useApexContext();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [projects, setProjects] = useState<ProjectItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [taskCount, setTaskCount] = useState(0);
  const [metricCount, setMetricCount] = useState(0);
  const [subtaskCount, setSubtaskCount] = useState(0);
  const [commentCount, setCommentCount] = useState(0);
  const [tasks, setTasks] = useState<TaskItem[]>([]);
  const [metrics, setMetrics] = useState<MetricItem[]>([]);
  const [subtasks, setSubtasks] = useState<SubtaskItem[]>([]);
  const [comments, setComments] = useState<CommentItem[]>([]);
  const [attachments, setAttachments] = useState<AttachmentItem[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatuses, setFilterStatuses] = useState<string[]>(DEFAULT_STATUSES);

  // Workflow states
  const [, setWorkflow] = useState<WorkflowData | null>(null);
  const [, setMeetings] = useState<MeetingData[]>([]);
  const [, setScoringEvals] = useState<ScoringData[]>([]);
  const [showWorkflowPanel, setShowWorkflowPanel] = useState(false);
  const [, setLoadingWorkflow] = useState(false);

  // Load vendors from database
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "vendorsPublic"), (snapshot) => {
      const vendorData: Vendor[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        vendorData.push({ id: doc.id, ...data } as Vendor);
      });
      // Sort by company name (first line of profile)
      vendorData.sort((a, b) => {
        const nameA = a.profile?.split("\n")[0] || "";
        const nameB = b.profile?.split("\n")[0] || "";
        return nameA.localeCompare(nameB);
      });
      setVendors(vendorData);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const projectsQuery = query(collection(db, "projects"), orderBy("createdAt", "desc"));
    const unsubscribe = onSnapshot(projectsQuery, (snapshot) => {
      const items = snapshot.docs.map((docItem) => {
        const data = docItem.data() as ProjectItem;
        return { ...data, id: docItem.id };
      });
      setProjects(items);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const unsubscribeTasks = onSnapshot(collection(db, "projectTasks"), (snapshot) => {
      setTaskCount(snapshot.size);
    });
    const unsubscribeMetrics = onSnapshot(collection(db, "projectMetrics"), (snapshot) => {
      setMetricCount(snapshot.size);
    });
    const unsubscribeSubtasks = onSnapshot(collection(db, "taskSubtasks"), (snapshot) => {
      setSubtaskCount(snapshot.size);
    });
    const unsubscribeComments = onSnapshot(collection(db, "comments"), (snapshot) => {
      setCommentCount(snapshot.size);
    });

    return () => {
      unsubscribeTasks();
      unsubscribeMetrics();
      unsubscribeSubtasks();
      unsubscribeComments();
    };
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    const tasksQuery = query(collection(db, "projectTasks"), where("projectId", "==", selectedId));
    const metricsQuery = query(collection(db, "projectMetrics"), where("projectId", "==", selectedId));
    const subtasksQuery = query(collection(db, "taskSubtasks"), where("projectId", "==", selectedId));
    const commentsQuery = query(collection(db, "comments"), where("projectId", "==", selectedId));
    const attachmentsQuery = query(collection(db, "attachments"), where("projectId", "==", selectedId));

    const unsubTasks = onSnapshot(tasksQuery, (snapshot) => {
      const items = snapshot.docs.map((docItem) => {
        const data = docItem.data() as TaskItem;
        return { ...data, id: docItem.id };
      });
      setTasks(items);
    });
    const unsubMetrics = onSnapshot(metricsQuery, (snapshot) => {
      const items = snapshot.docs.map((docItem) => {
        const data = docItem.data() as MetricItem;
        return { ...data, id: docItem.id };
      });
      setMetrics(items);
    });
    const unsubSubtasks = onSnapshot(subtasksQuery, (snapshot) => {
      const items = snapshot.docs.map((docItem) => {
        const data = docItem.data() as SubtaskItem;
        return { ...data, id: docItem.id };
      });
      setSubtasks(items);
    });
    const unsubComments = onSnapshot(commentsQuery, (snapshot) => {
      const items = snapshot.docs.map((docItem) => {
        const data = docItem.data() as CommentItem;
        return { ...data, id: docItem.id };
      });
      setComments(items);
    });
    const unsubAttachments = onSnapshot(attachmentsQuery, (snapshot) => {
      const items = snapshot.docs.map((docItem) => {
        const data = docItem.data() as AttachmentItem;
        return { ...data, id: docItem.id };
      });
      setAttachments(items);
    });

    return () => {
      unsubTasks();
      unsubMetrics();
      unsubSubtasks();
      unsubComments();
      unsubAttachments();
    };
  }, [selectedId]);

  const selectedProject = useMemo(
    () => projects.find((project) => project.id === selectedId) || null,
    [projects, selectedId]
  );

  // URL ?id= is the source of truth for the open detail modal — links from
  // briefs, chat, search results, and notifications can all deep-link, and the
  // back button works because closing the modal clears the param.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const id = params.get("id");
    if (id && projects.find((item) => item.id === id)) {
      setSelectedId(id);
      setIsModalOpen(true);
    } else if (!id && isModalOpen) {
      setIsModalOpen(false);
    }
  }, [location.search, projects]);

  // Apex: listen for open_entity broadcasts and route through the URL so the
  // back button works the same way as a clicked link.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as
        | { type?: string; id?: string }
        | undefined;
      if (!detail || detail.type !== "project" || !detail.id) return;
      navigate(`/projects?id=${encodeURIComponent(detail.id)}`);
    };
    window.addEventListener("apex:openEntity", handler as EventListener);
    return () => window.removeEventListener("apex:openEntity", handler as EventListener);
  }, [navigate]);

  // Apex: keep page-context filters in sync.
  useEffect(() => {
    setPageContext({
      filters: {
        searchQuery,
        statuses: filterStatuses,
      },
    });
  }, [searchQuery, filterStatuses, setPageContext]);

  // Apex: surface the currently-open project as openEntity.
  useEffect(() => {
    if (!isModalOpen || !selectedProject) {
      setPageContext({ openEntity: undefined });
      return;
    }
    setPageContext({
      openEntity: {
        type: "project",
        id: selectedProject.id,
        name: selectedProject.name,
        status: selectedProject.status,
      },
    });
    return () => setPageContext({ openEntity: undefined });
  }, [isModalOpen, selectedProject, setPageContext]);

  const loadWorkflowData = async (intakeId: string) => {
    if (!intakeId) return;
    setLoadingWorkflow(true);
    try {
      // getWorkflow returns null when the intake has no workflow yet (404).
      const data = await getWorkflow(intakeId);
      setWorkflow(data?.workflow ?? null);
      setMeetings(data?.meetings || []);
      setScoringEvals(data?.evaluations || []);
    } catch (error) {
      console.error("Failed to load workflow:", error);
    } finally {
      setLoadingWorkflow(false);
    }
  };

  useEffect(() => {
    if (selectedProject?.intakeId && showWorkflowPanel) {
      loadWorkflowData(selectedProject.intakeId);
    }
  }, [selectedProject?.intakeId, showWorkflowPanel]);

  // OAIP-114: debounced batched save through the audited backend handler.
  // Per-field onChange merges into `pendingUpdatesRef`. An 800ms timer
  // collapses a burst of edits into a single PATCH so the `auditLog` gets
  // one entry per editing session instead of one per keystroke. Status
  // changes (point-in-time choices via Select) flush immediately via
  // `handleUpdateProjectWithConfirm` rather than waiting on the timer.
  const pendingUpdatesRef = useRef<{
    projectId: string;
    updates: Record<string, unknown>;
  } | null>(null);
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inflightRef = useRef<Promise<void> | null>(null);

  const flushPendingUpdates = useCallback(async (): Promise<void> => {
    if (flushTimerRef.current) {
      clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
    const pending = pendingUpdatesRef.current;
    if (!pending || Object.keys(pending.updates).length === 0) {
      pendingUpdatesRef.current = null;
      return;
    }
    pendingUpdatesRef.current = null;
    // Serialise concurrent flushes so a force-flush during a debounced
    // flush doesn't double-send.
    if (inflightRef.current) {
      await inflightRef.current;
    }
    const send = (async () => {
      try {
        await updateProject(pending.projectId, pending.updates);
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Failed to save edits";
        message.error(msg);
        // Re-queue so the user can retry by editing again; without this the
        // edit is lost silently on the next blur. Merge with anything that
        // came in while the flush was in flight.
        const next = pendingUpdatesRef.current;
        pendingUpdatesRef.current = {
          projectId: pending.projectId,
          updates: { ...pending.updates, ...(next?.updates || {}) },
        };
      }
    })();
    inflightRef.current = send;
    try {
      await send;
    } finally {
      inflightRef.current = null;
    }
  }, [message]);

  const handleUpdateProject = useCallback(
    async (updates: Partial<ProjectItem>): Promise<void> => {
      if (!selectedProject) return;
      // If a different project was being edited, flush its pending edits
      // first so they're not merged into the new project's PATCH.
      if (
        pendingUpdatesRef.current &&
        pendingUpdatesRef.current.projectId !== selectedProject.id
      ) {
        await flushPendingUpdates();
      }
      const merged = {
        ...(pendingUpdatesRef.current?.updates || {}),
        ...(updates as Record<string, unknown>),
      };
      pendingUpdatesRef.current = {
        projectId: selectedProject.id,
        updates: merged,
      };
      // Status changes are point-in-time decisions (the user already paused
      // for the confirm dialog) — flush immediately so the backend writes
      // the auto-comment + status_transition audit entry without waiting
      // on the text-edit debounce.
      if ("status" in updates) {
        await flushPendingUpdates();
        return;
      }
      if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
      flushTimerRef.current = setTimeout(() => {
        void flushPendingUpdates();
      }, 800);
    },
    [selectedProject, flushPendingUpdates]
  );

  // Force-flush whenever the modal closes / selection changes / page
  // unmounts so no edits are silently lost.
  useEffect(() => {
    if (!isModalOpen) {
      void flushPendingUpdates();
    }
  }, [isModalOpen, flushPendingUpdates]);

  useEffect(() => {
    return () => {
      void flushPendingUpdates();
    };
  }, [flushPendingUpdates]);

  const openProjectModal = (projectId: string) => {
    setShowWorkflowPanel(false);
    navigate(`/projects?id=${encodeURIComponent(projectId)}`);
  };

  const filteredProjects = useMemo(() => {
    return projects.filter(project => {
      // Get vendor display name for search
      const vendorDisplayName = project.vendorName ? 
        vendors.find(v => v.id === project.vendorName)?.profile?.split("\n")[0] || "" 
        : "";
      
      const matchesSearch = project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           (project.source || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
                           vendorDisplayName.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = filterStatuses.length === 0 || filterStatuses.includes(project.status);
      return matchesSearch && matchesStatus;
    });
  }, [projects, searchQuery, filterStatuses, vendors]);

  // Helper to get vendor display name
  const getVendorDisplayName = (vendorId?: string) => {
    if (!vendorId) return "—";
    const vendor = vendors.find(v => v.id === vendorId);
    return vendor?.profile?.split("\n")[0] || "—";
  };

  // Map workflow status → semantic tone for the StatusPill. Collapses the
  // 9-color AntD Tag rainbow into 4 tones tied to DIT-approved score bands.
  const getStatusTone = (status: string): StatusTone => {
    if (!status) return "neutral";
    if (status.startsWith("Rejected")) return "danger";
    if (status.startsWith("On Hold")) return "warning";
    if (status === "Completed" || status === "Completed, Story Told") return "success";
    if (status === "In Progress") return "info";
    return "neutral";
  };

  const isDefaultStatusFilter = useMemo(() => {
    if (filterStatuses.length !== DEFAULT_STATUSES.length) return false;
    const set = new Set(filterStatuses);
    return DEFAULT_STATUSES.every((status) => set.has(status));
  }, [filterStatuses]);

  // Hide columns that hold no information for the current dataset. Source +
  // Vendor are routinely empty for accelerator projects; rendering an "—" in
  // every cell wastes table width without telling the user anything.
  // Note: `vendorName` is a vendor ID — checking the resolved display name so
  // we don't keep the column open just because a stale vendor ID is set.
  const hasAnySource = filteredProjects.some((p) => Boolean(p.source));
  const hasAnyVendor = filteredProjects.some(
    (p) => getVendorDisplayName(p.vendorName) !== "—"
  );

  const columns = [
    {
      title: "Project Name",
      dataIndex: "name",
      key: "name",
      render: (_: string, record: ProjectItem) => (
        <Space direction="vertical" size={0}>
          <Space size="small">
            <Typography.Text strong>{record.name}</Typography.Text>
            {record.intakeId && <Tag>Intake</Tag>}
          </Space>
        </Space>
      ),
    },
    ...(hasAnySource
      ? [
          {
            title: "Source",
            dataIndex: "source",
            key: "source",
            render: (source?: IntakeSource) =>
              source ? (
                <Tag>{source}</Tag>
              ) : (
                <Typography.Text type="secondary">—</Typography.Text>
              ),
          },
        ]
      : []),
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) => (
        <StatusPill label={status || "Unknown"} tone={getStatusTone(status)} inline />
      ),
    },
    ...(hasAnyVendor
      ? [
          {
            title: "Vendor",
            dataIndex: "vendorName",
            key: "vendor",
            render: (vendorName: string | undefined) => getVendorDisplayName(vendorName),
          },
        ]
      : []),
    {
      title: "Start date",
      dataIndex: "startDate",
      key: "startDate",
      className: "apex-col-date",
      render: (startDate: string | undefined) =>
        startDate ? new Date(startDate).toLocaleDateString() : "—",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: unknown, record: ProjectItem) => (
        <Space>
          <Button
            type="link"
            aria-label={`View project ${record.name}`}
            onClick={(event) => {
              event.stopPropagation();
              openProjectModal(record.id);
            }}
          >
            View
          </Button>
        </Space>
      ),
    },
  ];

  const inProgressCount = projects.filter((p) => p.status === "In Progress").length;
  const completedCount = projects.filter(
    (p) => p.status === "Completed" || p.status === "Completed, Story Told"
  ).length;
  const onHoldCount = projects.filter((p) => p.status?.includes("On Hold")).length;

  return (
    <>
      <div className="apex-page">
        {/* Page header */}
        <div className="apex-page-header">
          <div>
            <p className="apex-eyebrow">Portfolio</p>
            <h1 className="apex-page-title" style={{ marginTop: 6 }}>
              Projects
            </h1>
            <p className="apex-page-subtitle">
              Manage active initiatives and AI projects across OAIP.
            </p>
          </div>
        </div>

        {/* Compact metric strip — replaces the 9-stat Overview card. */}
        <section
          className="apex-surface"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 32,
            padding: "20px 24px",
            flexWrap: "wrap",
          }}
        >
          <div className="apex-metric">
            <span className="apex-metric__label">Total</span>
            <span className="apex-metric__value">{projects.length}</span>
          </div>
          <div className="apex-metric-divider" />
          <div className="apex-metric">
            <span className="apex-metric__label">In progress</span>
            <span className="apex-metric__value">{inProgressCount}</span>
          </div>
          <div className="apex-metric-divider" />
          <div className="apex-metric">
            <span className="apex-metric__label">Completed</span>
            <span className="apex-metric__value">{completedCount}</span>
          </div>
          <div className="apex-metric-divider" />
          <div className="apex-metric">
            <span className="apex-metric__label">On hold</span>
            <span className="apex-metric__value">{onHoldCount}</span>
          </div>
          <div className="apex-metric-divider" />
          <div className="apex-metric">
            <span className="apex-metric__label">Tasks &middot; metrics</span>
            <span className="apex-metric__value" style={{ fontSize: 22 }}>
              {taskCount}
              <span style={{ color: "var(--apex-ink-subtle)", fontWeight: 400 }}>
                {" "}/{" "}
              </span>
              {metricCount}
            </span>
            <span className="apex-metric__detail">
              {subtaskCount} subtasks &middot; {commentCount} comments
            </span>
          </div>
        </section>

        {/* Toolbar + table on one continuous surface. */}
        <div>
          <div className="apex-toolbar">
            <Input
              placeholder="Search projects..."
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              allowClear
              aria-label="Search projects"
              style={{ minWidth: 240, maxWidth: 320, flex: 1 }}
            />
            <Select
              mode="multiple"
              placeholder="All statuses"
              value={filterStatuses}
              onChange={(value) => setFilterStatuses(value)}
              aria-label="Filter projects by status"
              options={FLAT_STATUS_OPTIONS.map((status) => ({ value: status, label: status }))}
              // Don't render selected statuses as chips — at default 8+
              // selections they overflow the toolbar and look like
              // active filtering even when the user hasn't touched the
              // filter. Show one summary chip instead.
              maxTagCount={0}
              maxTagPlaceholder={(omitted) =>
                omitted.length === FLAT_STATUS_OPTIONS.length
                  ? "All statuses"
                  : `${omitted.length} selected`
              }
              showSearch={false}
              style={{ minWidth: 200, maxWidth: 260, flex: 1 }}
            />
            {!isDefaultStatusFilter && (
              <Button size="small" onClick={() => setFilterStatuses(DEFAULT_STATUSES)}>
                Reset (hide closed)
              </Button>
            )}
            <span style={{ marginLeft: "auto", fontSize: 12, color: "var(--apex-ink-muted)" }}>
              {filteredProjects.length} of {projects.length}
            </span>
          </div>

          <div className="apex-table-wrap">
            {loading ? (
              <div style={{ padding: 16 }}>
                <TableSkeleton rows={5} columns={6} />
              </div>
            ) : filteredProjects.length === 0 ? (
              <EmptyState
                description={
                  searchQuery || !isDefaultStatusFilter
                    ? "No projects match your filters"
                    : "No projects yet. New projects come from agency or vendor intakes."
                }
              />
            ) : (
              <Table
                rowKey="id"
                columns={columns}
                dataSource={filteredProjects}
                pagination={{ pageSize: 10, showSizeChanger: false }}
                aria-label="Projects directory"
                caption={
                  // Visually-hidden caption for screen readers; AntD renders the
                  // <caption> element which will absorb the screen-reader-only
                  // styling we apply via CSS.
                  "Project portfolio table"
                }
                onRow={(record) => ({
                  onClick: () => openProjectModal(record.id),
                  style: { cursor: "pointer" },
                })}
              />
            )}
          </div>
        </div>
      </div>

      {isModalOpen && selectedProject && (
        <ProjectDetailsModal
          project={selectedProject}
          tasks={tasks}
          metrics={metrics}
          subtasks={subtasks}
          comments={comments}
          attachments={attachments}
          onClose={() => navigate("/projects")}
          onUpdateProject={handleUpdateProject}
        />
      )}
    </>
  );
}
